import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../components/ui/sheet';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { Search, Eye } from 'lucide-react';
import { api } from '../../utils/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const Leads = () => {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [updateNotes, setUpdateNotes] = useState('');
  const [updateStatus, setUpdateStatus] = useState('');

  const loadLeads = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      
      const data = await api.getLeads(params);
      setLeads(data.leads);
      setTotal(data.total);
    } catch (error) {
      toast.error('Erreur lors du chargement des leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLeads();
  }, [search, statusFilter]);

  const openLeadDetail = async (leadId) => {
    try {
      const lead = await api.getLead(leadId);
      setSelectedLead(lead);
      setUpdateNotes(lead.notes || '');
      setUpdateStatus(lead.status);
      setSheetOpen(true);
    } catch (error) {
      toast.error('Impossible de charger le lead');
    }
  };

  const handleUpdate = async () => {
    try {
      await api.updateLead(selectedLead.id, {
        status: updateStatus,
        notes: updateNotes
      });
      toast.success('Lead mis à jour');
      setSheetOpen(false);
      loadLeads();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      nouveau: { bg: 'bg-teal-100', text: 'text-teal-800', label: 'Nouveau' },
      contacté: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Contacté' },
      qualifié: { bg: 'bg-green-100', text: 'text-green-800', label: 'Qualifié' },
      rdv_planifié: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'RDV planifié' },
      gagné: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Gagné' },
      perdu: { bg: 'bg-red-100', text: 'text-red-800', label: 'Perdu' }
    };
    const cfg = config[status] || config.nouveau;
    return <Badge className={`${cfg.bg} ${cfg.text}`}>{cfg.label}</Badge>;
  };

  if (loading) {
    return <div className="text-muted-foreground">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold mb-2">Leads</h1>
        <p className="text-muted-foreground">Gestion de vos demandes d'estimation</p>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher (nom, email, téléphone, adresse)..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="leads-search-input"
              />
            </div>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-48" data-testid="leads-status-filter">
              <SelectValue placeholder="Tous les statuts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="nouveau">Nouveau</SelectItem>
              <SelectItem value="contacté">Contacté</SelectItem>
              <SelectItem value="qualifié">Qualifié</SelectItem>
              <SelectItem value="rdv_planifié">RDV planifié</SelectItem>
              <SelectItem value="gagné">Gagné</SelectItem>
              <SelectItem value="perdu">Perdu</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <p className="text-sm text-muted-foreground mt-2">
          {total} lead{total !== 1 ? 's' : ''} trouvé{total !== 1 ? 's' : ''}
        </p>
      </Card>

      {/* Leads Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b">
              <tr>
                <th className="text-left p-4 text-sm font-medium">Contact</th>
                <th className="text-left p-4 text-sm font-medium">Bien</th>
                <th className="text-left p-4 text-sm font-medium">Localisation</th>
                <th className="text-left p-4 text-sm font-medium">Statut</th>
                <th className="text-left p-4 text-sm font-medium">Date</th>
                <th className="text-left p-4 text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-muted-foreground">
                    Aucun lead trouvé
                  </td>
                </tr>
              ) : (
                leads.map((lead) => (
                  <tr key={lead.id} className="border-b hover:bg-muted/30">
                    <td className="p-4">
                      <p className="font-medium">{lead.first_name} {lead.last_name}</p>
                      <p className="text-sm text-muted-foreground">{lead.email}</p>
                      <p className="text-sm text-muted-foreground">{lead.phone}</p>
                    </td>
                    <td className="p-4">
                      <p className="font-medium">{lead.property_type}</p>
                      <p className="text-sm text-muted-foreground">
                        {lead.surface_m2}m² {lead.num_rooms ? `• ${lead.num_rooms} pièces` : ''}
                      </p>
                    </td>
                    <td className="p-4">
                      <p className="text-sm">{lead.address || '-'}</p>
                      <p className="text-sm text-muted-foreground">CP {lead.postal_code}</p>
                    </td>
                    <td className="p-4">
                      {getStatusBadge(lead.status)}
                    </td>
                    <td className="p-4 text-sm text-muted-foreground">
                      {lead.created_at ? format(new Date(lead.created_at), 'dd MMM yyyy', { locale: fr }) : '-'}
                    </td>
                    <td className="p-4">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openLeadDetail(lead.id)}
                        data-testid={`lead-view-${lead.id}`}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Voir
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Lead Detail Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {selectedLead && (
            <>
              <SheetHeader>
                <SheetTitle>
                  {selectedLead.first_name} {selectedLead.last_name}
                </SheetTitle>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                {/* Contact Info */}
                <div>
                  <h3 className="font-semibold mb-3">Contact</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">Email:</span> {selectedLead.email}
                    </div>
                    <div>
                      <span className="text-muted-foreground">Téléphone:</span> {selectedLead.phone}
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Property Info */}
                <div>
                  <h3 className="font-semibold mb-3">Bien</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">Type:</span> {selectedLead.property_type}
                    </div>
                    <div>
                      <span className="text-muted-foreground">Surface:</span> {selectedLead.surface_m2}m²
                    </div>
                    {selectedLead.num_rooms && (
                      <div>
                        <span className="text-muted-foreground">Pièces:</span> {selectedLead.num_rooms}
                      </div>
                    )}
                    <div>
                      <span className="text-muted-foreground">Adresse:</span> {selectedLead.address || 'Non renseignée'}
                    </div>
                    <div>
                      <span className="text-muted-foreground">Code postal:</span> {selectedLead.postal_code}
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Status Update */}
                <div>
                  <Label htmlFor="status">Statut</Label>
                  <Select value={updateStatus} onValueChange={setUpdateStatus}>
                    <SelectTrigger id="status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nouveau">Nouveau</SelectItem>
                      <SelectItem value="contacté">Contacté</SelectItem>
                      <SelectItem value="qualifié">Qualifié</SelectItem>
                      <SelectItem value="rdv_planifié">RDV planifié</SelectItem>
                      <SelectItem value="gagné">Gagné</SelectItem>
                      <SelectItem value="perdu">Perdu</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Notes */}
                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={updateNotes}
                    onChange={(e) => setUpdateNotes(e.target.value)}
                    rows={4}
                    placeholder="Ajouter des notes sur ce lead..."
                  />
                </div>

                <Button onClick={handleUpdate} className="w-full">
                  Enregistrer les modifications
                </Button>

                {/* Attribution */}
                {(selectedLead.utm_source || selectedLead.referrer) && (
                  <>
                    <Separator />
                    <div>
                      <h3 className="font-semibold mb-3">Attribution</h3>
                      <div className="space-y-2 text-sm">
                        {selectedLead.utm_source && (
                          <div>
                            <span className="text-muted-foreground">Source:</span> {selectedLead.utm_source}
                          </div>
                        )}
                        {selectedLead.utm_medium && (
                          <div>
                            <span className="text-muted-foreground">Medium:</span> {selectedLead.utm_medium}
                          </div>
                        )}
                        {selectedLead.utm_campaign && (
                          <div>
                            <span className="text-muted-foreground">Campagne:</span> {selectedLead.utm_campaign}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default Leads;
